import { ApiResponse } from '../../quotes/interfaces/quote-request.interface';

/**
 * Utilidades para type guards y validación runtime
 * Proporciona funciones para validar tipos en tiempo de ejecución
 */

/**
 * Type guard para verificar si un valor es un objeto (no null, no array, no primitivo)
 */
export function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Type guard para verificar si un valor es un string no vacío
 */
export function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

/**
 * Type guard para verificar si un valor es un número válido
 */
export function isValidNumber(value: unknown): value is number {
  return typeof value === 'number' && !isNaN(value) && isFinite(value);
}

/**
 * Type guard para verificar si un valor es un número entero positivo
 */
export function isPositiveInteger(value: unknown): value is number {
  return isValidNumber(value) && Number.isInteger(value) && value > 0;
}

/**
 * Type guard para verificar si un valor es un array
 */
export function isArray(value: unknown): value is unknown[] {
  return Array.isArray(value);
}

/**
 * Type guard para verificar si un valor es un array no vacío
 */
export function isNonEmptyArray<T>(value: unknown): value is T[] {
  return Array.isArray(value) && value.length > 0;
}

/**
 * Type guard para verificar si un valor es una fecha válida (string o Date)
 */
export function isValidDate(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const date = new Date(value);
  return !isNaN(date.getTime());
}

/**
 * Valida que un ApiResponse tenga la estructura correcta
 */
export function isValidApiResponse<T>(value: unknown): value is ApiResponse<T> {
  if (!isObject(value)) return false;
  
  const response = value as Record<string, unknown>;
  
  // Debe tener la propiedad 'succeeded'
  if (typeof response['succeeded'] !== 'boolean') return false;
  
  // Si succeeded es true, debe tener 'data'
  if (response['succeeded'] === true) {
    // data puede ser cualquier tipo (array, objeto, primitivo, etc.)
    return 'data' in response;
  }
  
  // Si succeeded es false, es válido (puede tener message y errors)
  return true;
}

/**
 * Valida que un ApiResponse tenga datos válidos (succeeded: true y data presente)
 */
export function isValidApiResponseWithData<T>(value: unknown): value is ApiResponse<T> & { succeeded: true; data: T } {
  if (!isValidApiResponse(value)) return false;
  return value.succeeded === true && 'data' in value;
}

/**
 * Type guard para verificar si un objeto tiene una propiedad específica
 */
export function hasProperty<K extends string>(
  obj: unknown,
  key: K
): obj is Record<K, unknown> {
  return isObject(obj) && key in obj;
}

/**
 * Type guard para verificar si un objeto tiene múltiples propiedades
 */
export function hasProperties<K extends string>(
  obj: unknown,
  keys: K[]
): obj is Record<K, unknown> {
  if (!isObject(obj)) return false;
  return keys.every(key => key in obj);
}
